'use client'

// Правила голосового ввода в поле (решение пользователя 2026-09-22; общие
// для «Спроси Turkido» и «Разговора с AI»):
// - каждая новая запись ЗАМЕНЯЕТ текст в поле;
// - если ученик сам нажал в поле (или печатал в нём) — распознанное
//   вставляется в место курсора (или вместо выделения), и следующие записи
//   продолжают оттуда, пока реплика не отправлена или поле не очищено;
// - клавиатура на телефоне — только по нажатию в поле: курсор мы сами не
//   ставим (setSelectionRange на iPhone фокусирует поле и открывает её).
// userCaret — выбранное учеником место; null — режим замены. Место
// запоминаем в момент действия ученика: на телефоне DOM-выделение после
// потери фокуса ненадёжно.
import { useCallback, useEffect, useState, type RefObject } from 'react'
import type { Caret } from '@/lib/insert-dictation'
import { useCoarsePointer } from '@/lib/use-coarse-pointer'

type Field = HTMLInputElement | HTMLTextAreaElement

function caretOf(el: Field): Caret | null {
  if (!el.value) return null
  const end = el.value.length
  return { start: el.selectionStart ?? end, end: el.selectionEnd ?? end }
}

export function useDictationCaret(ref: RefObject<Field | null>, isListening: boolean) {
  const [userCaret, setUserCaret] = useState<Caret | null>(null)
  const coarse = useCoarsePointer()

  // Для onChange / onSelect / onClick поля: ученик сам в поле. В пустом поле
  // выбирать нечего — режим замены.
  const rememberCaret = useCallback((el: Field) => {
    if (document.activeElement === el) setUserCaret(caretOf(el))
  }, [])

  // Перетаскивание курсора за «ручку» на телефоне даёт только
  // selectionchange (без click/keyup, на которые смотрит onSelect React).
  // Chrome шлёт его на само поле, Safari — на document: слушаем оба.
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const onChange = () => {
      if (document.activeElement === el) setUserCaret(caretOf(el))
    }
    el.addEventListener('selectionchange', onChange)
    document.addEventListener('selectionchange', onChange)
    return () => {
      el.removeEventListener('selectionchange', onChange)
      document.removeEventListener('selectionchange', onChange)
    }
  }, [ref])

  // После додиктовки ставим курсор в поле за вставленным текстом — чтобы
  // набор с клавиатуры продолжился с того же места. Только с мышью: на
  // сенсорном экране это открыло бы клавиатуру без нажатия в поле.
  useEffect(() => {
    if (isListening || coarse) return
    const el = ref.current
    if (el && userCaret && document.activeElement !== el) {
      try {
        el.setSelectionRange(userCaret.start, userCaret.end)
      } catch {}
    }
  }, [ref, isListening, coarse, userCaret])

  return { userCaret, setUserCaret, rememberCaret }
}

// Подпись под кнопками микрофона: что сделает следующая запись.
export function dictationHint(input: string, userCaret: Caret | null): string | undefined {
  if (!input.trim()) return undefined
  return userCaret
    ? 'Держите кнопку и говорите, текст вставится туда, где стоит курсор.'
    : 'Новая запись заменит текст в поле. Чтобы дописать, нажмите в поле на нужное место.'
}
